"use client";

import type React from "react";

import { useTranslation } from "react-i18next";
import { Loader2, WifiOff } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface SupportChatViewProps {
  isLoading: boolean;
  isConnected: boolean;
  embedUrl: string;
  iframeRef: React.RefObject<HTMLIFrameElement>;
}

export function SupportChatView({
  isLoading,
  isConnected,
  embedUrl,
  iframeRef,
}: SupportChatViewProps) {
  const { t } = useTranslation(["support", "common"]);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <div className="flex items-center gap-3">
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          <p className="text-muted-foreground text-sm">{t("connectingToSupport")}</p>
        </div>
      </div>
    );
  }

  if (!isConnected || !embedUrl) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4 text-center"
        >
          <div className="bg-destructive/10 text-destructive rounded-full p-3">
            <WifiOff className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{t("connectionError")}</h3>
            <p className="text-muted-foreground max-w-md text-sm">
              {t("connectionErrorDescription")}
            </p>
          </div>
          <Button className="mt-2" variant="outline" onClick={() => window.location.reload()}>
            <Icon className="mr-2 h-4 w-4" icon="lucide:refresh-cw" />
            {t("refresh", { ns: "common" })}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <iframe
        ref={iframeRef}
        allow="microphone; camera"
        className={cn(
          "h-full w-full rounded-2xl border-0 bg-transparent",
          "transition-opacity duration-300",
        )}
        loading="lazy"
        src={embedUrl}
        title="Crisp Support Chat"
      />
    </div>
  );
}
